import SideMenu from "../../ui/SideMenu";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import useFetchExchanges from "../../hooks/api/useFetchExchanges";
import {
  Button,
  Checkbox,
  Input,
  message,
  Skeleton,
  Spin,
  Typography,
} from "antd";
import styles from "../../styles/shared/connect-to-exchange/ExchangeConnectionSettingsMenu.module.css";
import useFetchIntegratedExchanges from "../../hooks/api/useFetchIntegratedExchanges";
import * as React from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { MdOutlineOpenInNew } from "react-icons/md";
import InputComponent from "../connect-exchange/InputComponent";
import { FaCopy } from "react-icons/fa6";
import {
  closeExchangeConnectionSettings,
  getExchangesInfo,
  getTrustedIps,
  integrateWithExchange,
} from "../../redux/exchange/actions";
import { closeSideMenu } from "../../redux/ui/uiSlice";
import { IoSearchOutline } from "react-icons/io5";
import { FaAngleLeft } from "react-icons/fa";

const { Text } = Typography;

const isExchangeConnected = (connectedExchanges, currentExchange) => {
  return connectedExchanges?.find(
    (integratedEx) => integratedEx.id === currentExchange,
  );
};

const ConnectToExchangeStepOne = ({ selectExchange, selectedExchange }) => {
  const {
    exchangeState: {
      exchangesLoading,
      exchangesError,
      exchanges,
      integratedData,
      integratedDataLoading,
      confirmedExchanges,
    },
  } = useSelector((state) => state);

  const [searchedExchange, setSearchedExchange] = useState("");

  const searchExchangeHandler = (e) => {
    setSearchedExchange(e.target.value.trimStart());
  };

  const loading = exchangesLoading || integratedDataLoading;

  // filter exchange if exchange already integrated and not expired or need ip update
  const exchangeList = confirmedExchanges
    // ?.filter(
    //   (ex) =>
    //     !integratedData?.find(
    //       (integratedEx) =>
    //         integratedEx.id === ex.id && integratedEx.status === "Active",
    //     ),
    // )
    ?.filter(
      (ex) =>
        ex?.name?.toLowerCase()?.includes(searchedExchange?.toLowerCase()) ||
        ex?.id?.toLowerCase()?.includes(searchedExchange?.toLowerCase()),
    );

  useFetchIntegratedExchanges();

  return (
    <>
      <h3 className={styles.title}>Choose Your Exchange</h3>
      {loading && (
        <div className={styles.loaderStatus}>
          <Spin size="small" />
          <span>Getting exchanges info...</span>
        </div>
      )}
      {!exchangesError && (
        <>
          <div className={styles.searchBox}>
            <IoSearchOutline />
            <Input
              id="search-exchange"
              value={searchedExchange}
              onChange={searchExchangeHandler}
              placeholder="Search Exchange..."
            />
          </div>
          <ul className={styles.exchangeList}>
            {loading ? (
              Array.from({ length: 16 }).map((_, i) => (
                <li key={i} className={styles.loader}>
                  <Skeleton.Node active />
                </li>
              ))
            ) : exchangeList?.length === 0 ? (
              <li className={styles.noExchange}>
                {searchedExchange ? "No matches found" : "Failed to load"}
              </li>
            ) : (
              exchangeList?.map((ex) => (
                <li
                  key={ex.id}
                  className={`${styles.exchangeItem} ${selectedExchange === ex?.id ? styles.selectedExchange : ""} ${isExchangeConnected(integratedData, ex?.id) ? styles.connected : ""}`}
                  onClick={() => selectExchange(ex.id)}
                >
                  <LazyLoadImage
                    src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/crypto_exchanges_logo/${ex?.id}.svg`}
                    alt={ex?.id}
                    // width="25px"
                    height="25px"
                    effect="blur"
                  />
                  <span>{ex?.name}</span>
                </li>
              ))
            )}
          </ul>
        </>
      )}
    </>
  );
};

export const ConnectToExchangeStepTwo = ({ targetExchange }) => {
  const {
    main: { userData },
    exchangeState: {
      integrateExchangeLoading,
      integrateExchangeMessage,
      integrateExchangeError,
      exchangesConnectionInfo,
      exchangesLoading,
      trustedIps,
      getTrustedIpsStatus,
    },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  const connectionInfo = exchangesConnectionInfo?.find(
    (item) => item.id === targetExchange,
  );

  const exchangeName = connectionInfo?.name;

  const [apiKey, setApiKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [isRestricted, setIsRestricted] = useState(false);
  const [isProcessed, setIsProcessed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const forcedInfo = connectionInfo?.mandatory;
  const optionalInfo = connectionInfo?.optional || "";
  const optionalList = optionalInfo?.split(";;");
  const isApiKeyRequired = forcedInfo?.includes("key");
  const isSecretKeyRequired = forcedInfo?.includes("secret");
  const isPassphraseRequired = forcedInfo?.includes("passphrase");

  const apiError = isSubmitted && isApiKeyRequired && !apiKey;
  const secretError = isSubmitted && isSecretKeyRequired && !secretKey;
  const passphraseError = isSubmitted && isPassphraseRequired && !passphrase;

  const isBtnDisabled =
    (isApiKeyRequired && !apiKey) ||
    (isSecretKeyRequired && !secretKey) ||
    (isPassphraseRequired && !passphrase);

  const isConnected =
    isProcessed &&
    !integrateExchangeLoading &&
    integrateExchangeMessage?.STATUS_CODE === 200 &&
    !integrateExchangeError;

  const goToTutorial = () => {
    window.open(connectionInfo?.video_url, "_blank");
  };

  const copyToClipboardHandler = () => {
    setIsRestricted(true);
    navigator.clipboard.writeText(trustedIps?.[0]);
    // return message.success("Copied to clipboard.");
  };

  const submitHandler = async () => {
    if (integrateExchangeLoading) return;
    setIsSubmitted(true);

    if (
      (isApiKeyRequired && !apiKey) ||
      (isSecretKeyRequired && !secretKey) ||
      (isPassphraseRequired && !passphrase)
    )
      return message.warning("Please complete the necessary fields.");

    const ip =
      targetExchange === "cryptocrispy"
        ? ""
        : isRestricted
          ? trustedIps?.[0]
          : "";

    await dispatch(
      integrateWithExchange(
        userData["custom:custom_username"],
        userData?.email,
        apiKey,
        secretKey,
        targetExchange,
        passphrase,
        // isRestricted,
        ip,
        exchangeName,
      ),
    ).then(() => {
      setIsProcessed(true);
    });
  };

  useEffect(() => {
    if (
      !exchangesConnectionInfo.length &&
      Object.keys(userData).length &&
      !exchangesLoading
    ) {
      dispatch(getExchangesInfo());
    }
  }, [exchangesConnectionInfo, userData, exchangesLoading]);

  useEffect(() => {
    if (
      userData &&
      Object.keys(userData)?.length &&
      !trustedIps.length &&
      getTrustedIpsStatus !== "error" &&
      getTrustedIpsStatus !== "loading"
    ) {
      dispatch(getTrustedIps());
    }
  }, [dispatch, trustedIps, userData, getTrustedIpsStatus]);

  useEffect(() => {
    if (isConnected) {
      dispatch(closeSideMenu());
    }
  }, [isConnected]);

  return (
    <div>
      {exchangesLoading ? (
        "loading"
      ) : (
        <div className={styles.form}>
          <h3 className={styles.title}>Enter Connection Info</h3>
          {connectionInfo && (
            <ul className={styles.stepsList}>
              <li className={styles.formTitle}>
                <strong>Connect keys securely</strong>{" "}
                <span onClick={goToTutorial}>
                  Full Guide <MdOutlineOpenInNew size={16} />
                </span>
              </li>
              {/*<li className={styles.stepsTitle}>*/}
              {/*  <h4>Create an API key on {exchangeName}</h4>*/}
              {/*</li>*/}
              {connectionInfo.steps.split(";;").map((step, i) => (
                <li className={styles.stepsItem} key={step}>
                  {i + 1}. {step}
                </li>
              ))}
              <li>
                (Note : When creating API key make sure to enable trading and
                reading info options. Do NOT enable withdrawal Option)
              </li>
            </ul>
          )}
          <InputComponent
            value={apiKey}
            label="API Key"
            setValue={setApiKey}
            error={apiError}
            customStyle={{ width: "100%" }}
          />

          {connectionInfo?.mandatory?.includes("secret") ? (
            <>
              <InputComponent
                value={secretKey}
                label="Secret Key"
                setValue={setSecretKey}
                error={secretError}
                customStyle={{ width: "100%" }}
              />
            </>
          ) : (
            ""
          )}

          {connectionInfo?.mandatory?.includes("passphrase") ? (
            <>
              <InputComponent
                value={passphrase}
                label="API Passphrase"
                setValue={setPassphrase}
                error={passphraseError}
                customStyle={{ width: "100%" }}
              />
            </>
          ) : (
            ""
          )}

          {optionalInfo && <h4 className={styles.optional}>Optional</h4>}
          {optionalList?.includes("secret") ? (
            <InputComponent
              value={secretKey}
              label="Secret Key"
              setValue={setSecretKey}
              customStyle={{ width: "100%" }}
            />
          ) : (
            ""
          )}

          {optionalList?.includes("api_passphrase") ? (
            <InputComponent
              value={passphrase}
              label="API Passphrase"
              setValue={setPassphrase}
              customStyle={{ width: "100%" }}
            />
          ) : (
            ""
          )}

          <div className={styles.restrictionBox}>
            <h4 className={styles.optional}>IP Whitelist Values</h4>
            <div className={styles.restrictionOption}>
              <Checkbox
                className={`${styles.checkbox} ${
                  isRestricted ? styles.hideBorder : ""
                }`}
                value={isRestricted}
                checked={isRestricted}
                onChange={() => setIsRestricted((prevState) => !prevState)}
              />
              <p style={{ color: "#888888" }}>
                Copy the IP value below and add it to your exchange. This value
                is required so that the connection continues uninterrupted.
              </p>
            </div>
            <div className={styles.ipBox} onClick={copyToClipboardHandler}>
              <span>{trustedIps?.[0]}</span>
              <Text
                copyable={{
                  icon: (
                    <span className={styles.copyBtn}>
                      <FaCopy style={{ marginRight: ".3rem" }} />
                      Copy to Clipboard
                    </span>
                  ),
                  text: trustedIps?.[0],
                  tooltips: ["Copy to clipboard", "Copied!"],
                  onCopy: copyToClipboardHandler,
                }}
                className={styles.copyOption}
              ></Text>
            </div>
          </div>

          <Button
            className={styles.submitBtn}
            disabled={isBtnDisabled}
            loading={integrateExchangeLoading}
            onClick={submitHandler}
          >
            Connect
          </Button>
        </div>
      )}
    </div>
  );
};

function ExchangeConnectionSettingsMenu() {
  const {
    exchangeState: { targetSettingExchange, exchangesConnectionInfo },
    ui: { openSideMenu },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  const [selectedExchange, setSelectedExchange] = useState(
    targetSettingExchange,
  );
  const [step, setStep] = useState(1);

  const exchangeName = exchangesConnectionInfo?.find(
    (ex) => ex?.id === selectedExchange,
  )?.name;

  useFetchExchanges();

  const selectExchange = (ex) => {
    setSelectedExchange(ex);
    setStep(2);
  };

  const closeMenu = () => {
    dispatch(closeExchangeConnectionSettings());
  };

  useEffect(() => {
    if (targetSettingExchange && openSideMenu) {
      setStep(2);
      setSelectedExchange(targetSettingExchange);
    } else {
      setStep(1);
      setSelectedExchange("");
    }
  }, [targetSettingExchange, openSideMenu]);

  return (
    <SideMenu
      scope="exchange-connection-settings"
      title={
        targetSettingExchange
          ? `${exchangeName || targetSettingExchange} Settings`
          : step === 1
            ? "Connect to Exchanges"
            : `Connect to ${exchangeName}`
      }
      className={styles.container}
      closeHandler={closeMenu}
    >
      {step === 1 && (
        <ConnectToExchangeStepOne
          selectedExchange={selectedExchange}
          selectExchange={selectExchange}
        />
      )}

      {step === 2 && !targetSettingExchange && (
        <span className={styles.backBtn} onClick={() => setStep(1)}>
          <FaAngleLeft /> Back
        </span>
      )}

      {step === 2 && (
        <ConnectToExchangeStepTwo targetExchange={selectedExchange} />
      )}
    </SideMenu>
  );
}

export default ExchangeConnectionSettingsMenu;
